import type { NextWebVitalsMetric } from 'next/app'

import config from 'src/config'
import { analyticsConfig } from 'src/functions/analyticsConfig'
import { getStoredCookieConsent } from 'src/functions/cookieConsent'
import { isNewsletterPrivatePath } from 'src/functions/newsletterPrivacy'

type WebVitalRating = 'good' | 'needs-improvement' | 'poor' | 'unknown'

type ClientConnection = {
  effectiveType?: string
  downlink?: number
  saveData?: boolean
}

type WebVitalPayload = {
  id: string
  name: string
  label: NextWebVitalsMetric['label']
  value: number
  rating: WebVitalRating
  startTime: number
  path: string
  host: string
  viewport: {
    width: number
    height: number
  }
  connection?: ClientConnection
  userAgent: string
  timestamp: string
}

const webVitalThresholds: Partial<Record<NextWebVitalsMetric['name'], [number, number]>> = {
  CLS: [0.1, 0.25],
  FCP: [1800, 3000],
  FID: [100, 300],
  INP: [200, 500],
  LCP: [2500, 4000],
  TTFB: [800, 1800],
}

let sessionSampled: boolean | null = null

const getSampleRate = () => {
  const sampleRate = Number(process.env.NEXT_PUBLIC_WEB_VITALS_SAMPLE_RATE)
  if (!Number.isFinite(sampleRate)) return 1

  return Math.max(0, Math.min(1, sampleRate))
}

const shouldReportWebVitals = () => {
  if (typeof window === 'undefined') return false
  if (isNewsletterPrivatePath(window.location.pathname)) return false
  if (analyticsConfig.tracker === 'none') return false

  try {
    if (getStoredCookieConsent() !== 'all') return false
  } catch {
    return false
  }

  if (sessionSampled === null) {
    sessionSampled = Math.random() <= getSampleRate()
  }

  return sessionSampled
}

const getMetricRating = (metric: NextWebVitalsMetric): WebVitalRating => {
  const thresholds = webVitalThresholds[metric.name]
  if (!thresholds) return 'unknown'

  if (metric.value <= thresholds[0]) return 'good'
  if (metric.value <= thresholds[1]) return 'needs-improvement'
  return 'poor'
}

const getRoundedMetricValue = (metric: NextWebVitalsMetric) => (
  metric.name === 'CLS'
    ? Math.round(metric.value * 1000)
    : Math.round(metric.value)
)

const getConnection = (): ClientConnection | undefined => {
  const connection = (navigator as Navigator & {
    connection?: ClientConnection
    mozConnection?: ClientConnection
    webkitConnection?: ClientConnection
  }).connection
    || (navigator as Navigator & { mozConnection?: ClientConnection }).mozConnection
    || (navigator as Navigator & { webkitConnection?: ClientConnection }).webkitConnection

  if (!connection) return undefined

  return {
    effectiveType: connection.effectiveType,
    downlink: connection.downlink,
    saveData: connection.saveData,
  }
}

const createPayload = (metric: NextWebVitalsMetric): WebVitalPayload => ({
  id: metric.id,
  name: metric.name,
  label: metric.label,
  value: metric.value,
  rating: getMetricRating(metric),
  startTime: metric.startTime,
  path: window.location.pathname,
  host: window.location.host,
  viewport: {
    width: window.innerWidth,
    height: window.innerHeight,
  },
  connection: getConnection(),
  userAgent: navigator.userAgent,
  timestamp: new Date().toISOString(),
})

const sendToShopEndpoint = (payload: WebVitalPayload) => {
  const body = JSON.stringify(payload)
  const endpoint = `${config.api_server.online}/web-vitals`

  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: 'application/json' })
    if (navigator.sendBeacon(endpoint, blob)) return
  }

  void fetch(endpoint, {
    method: 'POST',
    body,
    headers: {
      'Content-Type': 'application/json',
    },
    keepalive: true,
  }).catch(() => undefined)
}

const sendToGoogleAnalytics = (metric: NextWebVitalsMetric, rating: WebVitalRating) => {
  if (analyticsConfig.tracker !== 'google_analytics') return

  window.dataLayer = window.dataLayer || []
  window.gtag = window.gtag || ((...args) => {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(args)
  })

  window.gtag('event', 'web_vital', {
    metric_id: metric.id,
    metric_name: metric.name,
    metric_label: metric.label,
    metric_rating: rating,
    metric_value: metric.value,
    value: getRoundedMetricValue(metric),
    non_interaction: true,
  })
}

const sendToMatomo = (metric: NextWebVitalsMetric, rating: WebVitalRating) => {
  if (analyticsConfig.tracker !== 'matomo') return

  window._paq = window._paq || []
  window._paq.push([
    'trackEvent',
    'Web Vitals',
    metric.name,
    rating,
    getRoundedMetricValue(metric),
  ])
}

export const reportShopWebVital = (metric: NextWebVitalsMetric) => {
  if (!shouldReportWebVitals()) return

  const rating = getMetricRating(metric)
  const payload = createPayload(metric)

  sendToShopEndpoint(payload)
  sendToGoogleAnalytics(metric, rating)
  sendToMatomo(metric, rating)
}
